import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ClockIcon from '@/public/icons/approval/clock.svg';
import { isEmptyContent } from '@/utils/editor';
import sortApproversByOrder from '@/utils/approval/sortApproversByOrder';
import useContentHtmlPostprocess from './useContentHtmlPostprocess';
import {
  buildWithdrawalRejectionHtml,
  formatOpinionAt,
  normalizeLang,
  pickLang,
} from './statusMapping';
import {
  OpinionBoxAuthor,
  OpinionBoxBody,
  OpinionBoxHeader,
  OpinionBoxRoot,
  OpinionBoxTimestamp,
  OpinionSectionLabel,
  OpinionSectionRoot,
} from './styles';
import type { ApprovalLogTone } from './CommentSlot.types';
import type {
  ApprovalViewApprover,
  ApprovalViewAuthor,
  ApproverStatus,
} from './types';

/**
 * 결재자 의견 영역 — opinion 있는 결재자만 order 순 박스 렌더, 톤은 코멘트 로그 행과 동일.
 * 빈값 판정은 isEmptyContent(에디터 빈 마크업은 trim 으론 안 걸러져 빈 박스가 떴었음).
 * 코멘트 로그 행과의 중복 노출은 의도.
 * 작성자 명의 박스 2종이 맨 위 — 시간순으로 상신자 의견, 그다음 회수(반려). 둘은 동시 존재 가능.
 * 반려요청은 결재 결과가 아니고 노출 대상도 제한되므로 여기 두지 않는다 — 상단 ReturnRequestNotice 담당.
 */

const STATUS_TONE: Partial<Record<ApproverStatus, ApprovalLogTone>> = {
  C: 'confirm',
  R: 'reject',
  H: 'hold',
};

type OpinionBoxProps = {
  name: string;
  position: string;
  timestamp: string;
  tone: ApprovalLogTone;
  contentHtml: string | null;
};

const OpinionBox = ({
  name,
  position,
  timestamp,
  tone,
  contentHtml,
}: OpinionBoxProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  useContentHtmlPostprocess(contentRef, contentHtml);

  return (
    <OpinionBoxRoot tone={tone}>
      <OpinionBoxHeader>
        <OpinionBoxAuthor>
          {name} {position}
        </OpinionBoxAuthor>
        <OpinionBoxTimestamp>
          <ClockIcon />
          {timestamp}
        </OpinionBoxTimestamp>
      </OpinionBoxHeader>
      {contentHtml && (
        <OpinionBoxBody
          ref={contentRef}
          dangerouslySetInnerHTML={{ __html: contentHtml }}
        />
      )}
    </OpinionBoxRoot>
  );
};

const ApproverOpinionBox = ({
  approver,
  lang,
}: {
  approver: ApprovalViewApprover;
  lang: ReturnType<typeof normalizeLang>;
}) => {
  const contentHtml =
    approver.opinion && !isEmptyContent(approver.opinion)
      ? approver.opinion
      : null;

  return (
    <OpinionBox
      name={pickLang(approver.name, lang)}
      position={pickLang(approver.positionName, lang)}
      timestamp={formatOpinionAt(approver.approvedAt ?? approver.updatedAt)}
      tone={STATUS_TONE[approver.status] ?? 'default'}
      contentHtml={contentHtml}
    />
  );
};

type OpinionSlotProps = {
  approvers: ApprovalViewApprover[];
  /** 회수 시 채워지는 회수 사유. 작성자 명의 반려 박스로 표시. */
  withdrawalReason?: string | null;
  author?: ApprovalViewAuthor;
  /** 회수 시각 — 보통 문서 updatedAt. */
  withdrawnAt?: string | null;
  /** 상신 시 입력한 작성자 의견. v1 문서는 `<p>…</p>` HTML 로 내려옴. */
  authorOpinion?: string | null;
  /** 상신 시각 — 문서 authoredAt. */
  authoredAt?: string | null;
};

const OpinionSlot = ({
  approvers,
  withdrawalReason,
  author,
  withdrawnAt,
  authorOpinion,
  authoredAt,
}: OpinionSlotProps) => {
  const { t, i18n } = useTranslation();
  const lang = useMemo(() => normalizeLang(i18n.language), [i18n.language]);

  const withOpinion = useMemo(
    () =>
      sortApproversByOrder(
        approvers.filter((a) => a.opinion && !isEmptyContent(a.opinion)),
      ),
    [approvers],
  );

  const hasWithdrawal = Boolean(withdrawalReason && author);
  const hasAuthorOpinion = Boolean(
    author && authorOpinion && !isEmptyContent(authorOpinion),
  );
  if (withOpinion.length === 0 && !hasWithdrawal && !hasAuthorOpinion) {
    return null;
  }

  return (
    <OpinionSectionRoot>
      <OpinionSectionLabel>{t('결재자 의견')}</OpinionSectionLabel>
      {hasAuthorOpinion && author && (
        <OpinionBox
          name={pickLang(author.name, lang)}
          position={pickLang(author.positionName, lang)}
          timestamp={formatOpinionAt(authoredAt ?? null)}
          tone="default"
          contentHtml={authorOpinion ?? null}
        />
      )}
      {hasWithdrawal && author && (
        <OpinionBox
          name={pickLang(author.name, lang)}
          position={pickLang(author.positionName, lang)}
          timestamp={formatOpinionAt(withdrawnAt ?? null)}
          tone="reject"
          contentHtml={buildWithdrawalRejectionHtml(
            withdrawalReason ?? null,
            t,
          )}
        />
      )}
      {withOpinion.map((approver) => (
        <ApproverOpinionBox
          key={`opinion-${approver.passportId}-${approver.order}`}
          approver={approver}
          lang={lang}
        />
      ))}
    </OpinionSectionRoot>
  );
};

export default OpinionSlot;
